import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../../shared/Image/Image';
import { ThemeLink } from '../../shared/ThemeLink/ThemeLink';
import { fields } from '../../../lib/translation/strings';
import { NonNullListItem, SingleOrder } from '../../../queries/ordersQueries';
import { useMutation } from 'react-apollo';
import { UPDATE_ORDER_STATUS } from '../../../queries/mutations/checkoutMutation';
import { useNotification } from '../../../lib/context/NotificationContext/NotificationContext';
import { Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { ReactComponent as TimeLeft } from '../../../assets/icons/timeleft.svg';
import { useAuthContext } from '../../../lib/context/AuthContext/AuthContext';
import { Container } from '../../Container/Container';
import { Button } from '../../shared/Button/Button';
import { ProductPrices } from '../../shared/ProductPrices/ProductPrices';
import { useOrderPriceVisibility } from '../../../lib/hooks/useOrderPriceVisibility';

import './OrderSingleApproved.scss';
import { useWindowWidth } from '../../../lib/hooks/useWindowWidth';
import { removeHTMLEntities } from '../../../lib/helpers/removeHTMLEntities';
import { Quantity } from '../../shared/QuantityInput/Quantity';
import { ReactComponent as DeleteCart } from './../../../assets/icons/delete-cart.svg';
import { OrderFeedbackModal } from '../../OrderFeedback/OrderFeedbackModal';

const APPROVAL_WINDOW_SECONDS = 600;

type StoredOrder = {
  orderId: string;
  expiresAt?: number;
  timeLeft?: number;
  lastVisited?: number;
};

const parseAmount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value == null) {
    return 0;
  }

  const normalized = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|[\u00A0]/g, ' ')
    .replace(/[^\d,.-]/g, '')
    .replace(',', '.')
    .trim();
  const parsed = parseFloat(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value: unknown) => `${parseAmount(value).toFixed(2)}€`;

const getProductId = (item?: NonNullListItem | null) =>
  item?.product?.databaseId;

const readStoredOrders = (): StoredOrder[] => {
  try {
    const raw = localStorage.getItem('orders');
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeStoredOrders = (orders: StoredOrder[]) => {
  try {
    localStorage.setItem('orders', JSON.stringify(orders));
  } catch (e) {
    // Ignore quota / private mode failures.
  }
};

interface Props {
  orderList: NonNullListItem[];
  orderData: SingleOrder;
}

export const OrderSingleApproved = (props: Props) => {
  const { orderList: orderListItems, orderData } = props;
  const safeOrderItems = (orderListItems || []).filter(
    (item) => getProductId(item) != null
  );
  const [orderList, setRows] = useState(safeOrderItems);
  const [totalOrderValue, setTotalValue] = useState(
    () => orderData.total || formatAmount(0)
  );

  const { t } = useTranslation();
  const authCtx = useAuthContext();
  const { itemVisibility, canShowTotals } = useOrderPriceVisibility(orderList, {
    showWhenNoPriceCategory: true,
  });
  const navigation = useHistory();
  const ntfCtx = useNotification();
  const windowWidth = useWindowWidth();
  const isTablet = windowWidth <= 1024;
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const hasCancelledRef = useRef(false);
  const [mutation, { loading }] = useMutation(UPDATE_ORDER_STATUS);
  const [updatedList, setUpdatedList] = useState(() =>
    safeOrderItems.reduce((acc, item) => {
      const productId = getProductId(item);
      if (productId == null) {
        return acc;
      }

      acc[productId] = {
        removed: false,
        updatedStock: item.quantity || item.product?.quantity || 1,
      };
      return acc;
    }, {} as { [key: number]: { removed: boolean; updatedStock: number } })
  );

  const recalculateTotal = (items: NonNullListItem[]) => {
    const shipping = parseAmount(orderData?.shippingTotal);
    const itemsTotal = items.reduce(
      (sum, item) => sum + parseAmount(item.total),
      0
    );
    return formatAmount(shipping + itemsTotal);
  };

  useEffect(() => {
    if (timeLeft === 0 && !hasCancelledRef.current) {
      hasCancelledRef.current = true;
      cancelCurrentOrder(orderData.orderNumber);
    }
    // eslint-disable-next-line
  }, [timeLeft]);

  const cancelCurrentOrder = async (
    orderId: string,
    hasUserCanceled = false
  ) => {
    const userId = authCtx?.user?.userId;
    if (!orderId || userId == null) {
      ntfCtx.danger('Diçka shkoj keq ju lutemi provoni përsëri!', true);
      return;
    }

    try {
      const res = await mutation({
        variables: {
          orderId: parseInt(orderId, 10),
          status: 'CANCELLED',
          userId,
          items: JSON.stringify(updatedList),
        },
      });

      if (!res) {
        return;
      }

      removeOrder(orderId);
      navigation.push('/my-profile/orders');
      ntfCtx.warning(
        hasUserCanceled
          ? 'Porosia juaj është anuluar me sukses!'
          : 'Porosia juaj është anuluar sepse nuk keni kompletuar porosinë brenda kohës së caktuar!',
        true
      );
    } catch (error) {
      ntfCtx.danger('Diçka shkoj keq ju lutemi provoni përsëri!', true);
    }
  };

  const completeCurrentOrder = async (orderId: string) => {
    const userId = authCtx?.user?.userId;
    if (!orderId || userId == null) {
      ntfCtx.danger('Diçka shkoj keq ju lutemi provoni përsëri!', true);
      return;
    }

    try {
      const res = await mutation({
        variables: {
          orderId: parseInt(orderId, 10),
          status: 'COMPLETED',
          userId,
          items: JSON.stringify(updatedList),
        },
      });

      if (!res) {
        return;
      }

      removeOrder(orderId);
      setFeedbackOpen(true);
      ntfCtx.success('Porosia juaj është kompletuar me sukses!', true);
    } catch (error) {
      ntfCtx.danger('Diçka shkoj keq ju lutemi provoni përsëri!', true);
    }
  };

  const handleRemoveItem = (itemId: number) => {
    const updateList = orderList.filter(
      (item) => getProductId(item) !== itemId
    );
    setRows(updateList);
    setTotalValue(recalculateTotal(updateList));
    setUpdatedList((prevList) => ({
      ...prevList,
      [itemId]: {
        ...prevList[itemId],
        removed: true,
        updatedStock: prevList[itemId]?.updatedStock || 1,
      },
    }));
  };

  const updateStockQuantity = (quantity: number, itemId: number) => {
    const nextQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

    setUpdatedList((prevList) => ({
      ...prevList,
      [itemId]: {
        ...prevList[itemId],
        removed: prevList[itemId]?.removed ?? false,
        updatedStock: nextQuantity,
      },
    }));

    const updateList = orderList.map((item) => {
      if (getProductId(item) !== itemId) {
        return item;
      }

      return {
        ...item,
        quantity: nextQuantity,
        total: nextQuantity * parseAmount(item.price),
      };
    });
    setRows(updateList);
    setTotalValue(recalculateTotal(updateList));
  };

  useEffect(() => {
    const orderId = orderData.orderNumber;
    if (!orderId) {
      return;
    }

    let expiresAt = 0;
    if (orderData.approvedAt) {
      const approvedMs =
        orderData.approvedAt > 1e12
          ? orderData.approvedAt
          : orderData.approvedAt * 1000;
      expiresAt = approvedMs + APPROVAL_WINDOW_SECONDS * 1000;
    } else {
      const stored = readStoredOrders();
      const existing = stored.find((order) => order.orderId === orderId);

      if (existing?.expiresAt) {
        expiresAt = existing.expiresAt;
      } else if (existing?.timeLeft && existing?.lastVisited) {
        expiresAt = existing.lastVisited + existing.timeLeft * 1000;
      } else {
        expiresAt = Date.now() + APPROVAL_WINDOW_SECONDS * 1000;
      }

      writeStoredOrders([
        ...stored.filter((order) => order.orderId !== orderId),
        {
          orderId,
          expiresAt,
          timeLeft: APPROVAL_WINDOW_SECONDS,
          lastVisited: Date.now(),
        },
      ]);
    }

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((expiresAt - Date.now()) / 1000)
      );
      setTimeLeft(Number.isFinite(remaining) ? remaining : 0);
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [orderData.orderNumber, orderData.approvedAt]);

  function formatTimeLeft(secondsLeft: number) {
    const safeSeconds = Number.isFinite(secondsLeft) ? Math.max(0, secondsLeft) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  const removeOrder = (orderId: string) => {
    writeStoredOrders(
      readStoredOrders().filter((order) => order.orderId !== orderId)
    );
  };

  if (loading && !feedbackOpen) {
    return (
      <div className="OrderSingleApproved__wrapper">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 70,
          }}
        >
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="OrderSingleApproved__wrapper">
      <div className="OrderSingleApproved">
        <div className="ProfileOrdersSingle__content">
          <div className="OrderSingleApproved__title">
            <span className="ProfileOrdersSingle__order-nr">
              <strong>{t(fields.order)}: </strong>#{orderData?.orderNumber}
            </span>

            {!isTablet && (
              <div className="tableHead">
                <span className="OrderSingleApproved__quantity_title">
                  Sasia
                </span>
                {canShowTotals && (
                  <>
                    <span className="OrderSingleApproved__quantity_title OrderSingleApproved__quantity_title_price">
                      Çmimi
                    </span>
                    <span className="OrderSingleApproved__quantity_title OrderSingleApproved__quantity_title_value">
                      Vlera
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="ProfileOrdersSingle__left-side">
            {orderList?.map((item: NonNullListItem, index: number) => {
              const productId = getProductId(item);
              if (productId == null) {
                return null;
              }

              const stockQuantity = item.quantity || item.product?.quantity || 1;
              return isTablet ? (
                <div key={index} className="CartItem">
                  <div className="CartItem__wrapper">
                    <div className="CartItem__wrapper__left">
                      <div className="CartItem__image">
                        {item.product?.image && (
                          <ThemeLink to={`/product/${item.product?.slug}`}>
                            <Image
                              dynamicRef={true}
                              src={item?.product?.image?.sourceUrl}
                              srcSet={item?.product?.image?.sourceUrl}
                              alt={item.product.name}
                            />
                          </ThemeLink>
                        )}
                      </div>
                      <div className="CartItem__content">
                        <div className="CartItem__content_txt">
                          <ThemeLink to={`/product/${item.product?.slug}`}>
                            <h3 className="CartItem__content__title">
                              {item.product?.name}
                            </h3>
                          </ThemeLink>
                          {/* <span>{stockQuantity} në stok</span> */}
                        </div>
                      </div>
                    </div>
                    <div className="CartItem__wrapper__right">
                      <div className="CartItem__wrapper__right--m">
                        <span className="CartItem__wrapper__right--m-span">
                          Sasia
                        </span>
                        <div className="HowTo__content_big_card_desc_label">
                          <div className="HowTo__content_big_card_desc_label_minutes">
                          <Quantity 
                              maxQuantity={item.product?.quantity || stockQuantity}
                              quantity={item?.quantity || 1}
                              onChange={(value) => updateStockQuantity(value, productId)}
                            />
                          </div>
                        </div>
                      </div>
                      {itemVisibility[index] && (
                      <div className="CartItem__wrapper__right--m">
                        <span className="CartItem__wrapper__right--m-span">
                          Çmimi
                        </span>
                        <div className="HowTo__content_big_card_desc_label">
                          <div className="HowTo__content_big_card_desc_label_minutes">
                            <ProductPrices
                              regular={`${item?.price}€`}
                              sale={item.product?.salePrice}
                              onSale={item.product?.onSale}
                            />
                          </div>
                        </div>
                      </div>
                      )}
                     
                      <div className="CartItem__wrapper__right--m">
                        {itemVisibility[index] && (
                        <span className="CartItem__wrapper__right--m-span">
                          Vlera
                        </span>
                        )}
                        <div className="CartItem__attributes__quantity">
                          {itemVisibility[index] ? formatAmount(item.total) : null}
                          <button
                            id="addToCardBtn"
                            className="btn--addtocart btn--addtocart--reverse"
                            onClick={() => handleRemoveItem(productId)}
                            disabled={false}
                          >
                            {loading ? (
                              <Spinner
                                style={{
                                  color: '#fff',
                                  width: '1.25rem',
                                  height: '1.25rem',
                                  borderWidth: '.2em',
                                  margin: 'auto',
                                }}
                              />
                            ) : (
                              <div className="d-flex justify-content-center align-items-center" style={{background:'#fff'}}>
                                <DeleteCart
                                  fill={'red'}
                                  width={20}
                                  height={20}
                                />
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={index} className="CartItem">
                  <div className="CartItem__wrapper">
                    <div className="CartItem__wrapper__l_r">
                      <div className="CartItem__wrapper__left">
                        <div className="CartItem__image">
                          {item.product?.image && (
                            <ThemeLink to={`/product/${item.product?.slug}`}>
                              <Image
                                dynamicRef={true}
                                src={item?.product?.image?.sourceUrl}
                                srcSet={item?.product?.image?.sourceUrl}
                                alt={item.product.name}
                              />
                            </ThemeLink>
                          )}
                        </div>
                        <div className="CartItem__content">
                          <div className="CartItem__content_txt">
                            <ThemeLink to={`/product/${item.product?.slug}`}>
                              <h3 className="CartItem__content__title">
                                {item.product?.name}
                              </h3>
                            </ThemeLink>
                            {/* <span>{stockQuantity} në stok</span> */}
                          </div>
                        </div>
                      </div>
                      <div className="CartItem__wrapper__right">
                        <div className="HowTo__content_big_card_desc_label">
                          <div className="HowTo__content_big_card_desc_label_minutes">
                            <Quantity 
                              maxQuantity={item.product?.quantity || stockQuantity}
                              quantity={item?.quantity || 1}
                              onChange={(value) => updateStockQuantity(value, productId)}
                            />
                          </div>
                        </div>
                        {itemVisibility[index] && (
                        <div className="HowTo__content_big_card_desc_label">
                          <div className="HowTo__content_big_card_desc_label_minutes">
                            <ProductPrices
                              regular={`${item?.price}€`}
                              sale={item.product?.salePrice}
                              onSale={item.product?.onSale}
                            />
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                    <div className="CartItem__attributes__quantity">
                      {itemVisibility[index] && (
                      <span className="price">
                        {formatAmount(item.total)}
                      </span>
                      )}
                      <button
                        id="addToCardBtn"
                        className="btn--addtocart btn--addtocart--reverse"
                        onClick={() => handleRemoveItem(productId)}
                        disabled={false}
                      >
                        {loading ? (
                          <Spinner
                            style={{
                              color: '#fff',
                              width: '1.25rem',
                              height: '1.25rem',
                              borderWidth: '.2em',
                              margin: 'auto',
                            }}
                          />
                        ) : (
                          <div className="d-flex justify-content-center align-items-center" style={{background:'#fff'}}>
                            <DeleteCart
                              fill={'red'}
                              width={20}
                              height={20}
                            />
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="CartItem">
              <div className="CartItem__wrapper">
                <div className="CartItem__content">
                  <div className="CartItem__content_txt CartItem__content_txt--custom">
                    <h3 className="CartItem__content__title">Transporti</h3>
                    <span>
                      {canShowTotals
                        ? removeHTMLEntities(orderData?.shippingTotal)
                        : null}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {timeLeft !== 0 && timeLeft !== null && (
        <div className="CartTotal">
          <Container>
            <div className="CartTotal__wrapper__approved">
              <div className="CartSidebar-footer">
                <div className="CartSidebar-footer--left">
                  <span>Çmimi total</span>
                  {canShowTotals && (
                    <h1
                      dangerouslySetInnerHTML={{
                        __html: String(totalOrderValue || ''),
                      }}
                    />
                  )}
                </div>
                <Button
                  color="secondary"
                  title=""
                  onClick={() =>
                    cancelCurrentOrder(orderData.orderNumber, true)
                  }
                  style={{ marginRight: 8 }}
                >
                  Anulo
                </Button>
                <Button
                  color="primary"
                  title=""
                  onClick={() => completeCurrentOrder(orderData.orderNumber)}
                  className="FeedbackModal__button"
                >
                  Vazhdo me porosinë
                </Button>
              </div>
              <div className="CartTotal__wrapper__approved--time d-flex align-items-center">
                <TimeLeft style={{ marginRight: 7 }} />
                <span>{formatTimeLeft(timeLeft)} minuta</span>
              </div>
            </div>
          </Container>
        </div>
      )}
      <OrderFeedbackModal
        orderId={orderData?.orderNumber}
        open={feedbackOpen}
        toggle={() => {
          setFeedbackOpen(false);
          navigation.push('/my-profile/orders');
        }}
      />
    </div>
  );
};
